import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { useEstimatePriceMutation, useLazyGetCardByIdQuery } from 'store/services/cards/search/cardsSearchApi';
import { SetDetail } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import { FoilCardImage } from 'components/FoilCardImage';
import './InitiateTradeModal.css';
import { finishFromRow, FINISH_LABELS, FinishSource, finishFields, fromHaveFinish, getAvailableFinishes, HaveFinish, haveFinishFromRow, offerableFinishes, toHaveFinish } from 'utils/finishes';
import { FinishSelect } from 'components/FinishSelect';
import { NoPrice, NO_PRICE_BLOCKS_TRADE } from 'components/NoPrice/NoPrice';
import { hasRealPrice, sumRealPrices } from 'utils/price';
import { SetIcon } from 'components/SetIcon';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import SendPointsModal from 'components/SendPointsModal/SendPointsModal';
import { useCardHoverPreview } from 'hooks/useCardHoverPreview';
import { useIsMobile } from 'hooks/useIsMobile';
import { getFundingState, SEND_POINTS_HELPER_MAX_DEFICIT } from 'utils/fundingState';
import { setIconUrl } from 'utils/setIconUrl';

export interface TradeCard {
  id: string;
  qty: number;
  maxQty: number;  // Maximum quantity the receiver wants
  name: string;
  set: string;  // Current set name (for display)
  setCode: string;  // Current set code (for API calls)
  condition: string;
  language: string;
  /** Derived mirror of `finish` (finishFields) */
  foil: boolean;
  /** Authoritative finish of the copy being sent (Track D) */
  finish?: HaveFinish;
  /** The finish this row opened with; drives the D-9 delta line */
  initialFinish?: HaveFinish;
  edition: string;
  image: string;
  imageSmall?: string;
  /** Null when the row has no price; read it through hasRealPrice (0 is no price too). */
  value: number | null;
  baseValue: number;  // Original value before modifications
  foilValue?: number;
  nonFoilValue?: number;
  etchedValue?: number | null;
  /** Finishes the displayed printing exists in (lowercase) */
  availableFinishes?: string[];
  acceptableSets?: string[];  // Set names the receiver will accept (for display)
  acceptableSetCodes?: string[];  // Set codes the receiver will accept (for API)
  acceptableSetDetails?: SetDetail[];  // Combined set info with enhanced data
  wantListId?: number;  // Added for API calls
  availableInFoil?: boolean;  // Whether this card is available in foil
  availableInNonFoil?: boolean;  // Whether this card is available in non-foil
  acceptFoil?: boolean;  // Whether the receiver accepts foil
  acceptNonFoil?: boolean;  // Whether the receiver accepts non-foil
  acceptEtched?: boolean | null;  // Whether the receiver accepts etched (Track D)
  cardId?: number;  // Track selected card ID for specific version
  scryfallId?: string;  // Scryfall UUID for API calls (estimate-price, card detail)
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;  // Language variants for this printing
  acceptableLanguages?: string[];  // Languages the receiver accepts (from want record)
  acceptableConditions?: string[];  // Conditions the receiver accepts
  sourceCardId?: string;  // Original card.id from the package (for SendPage2 lookup)
}

interface Props {
  isOpen: boolean;
  cards: TradeCard[];
  onClose: () => void;
  onInitiateTrade: (cards: TradeCard[]) => void;
  isLoading?: boolean;
  lockId?: string;
  lockExpiresAt?: string;
  /** The receiver's user id: Send Points needs it. Without it the Send Points entry points are not shown. */
  receiverId?: number;
  receiverUsername?: string;
  receiverAvatarUrl?: string;
  receiverPoints?: number;
  receiverCountry?: string;
  haveListQuantityByCardIdMap?: Map<number, number>;
}

export default function InitiateTradeModal({ isOpen, cards, onClose, onInitiateTrade, isLoading = false, lockId, lockExpiresAt, receiverId, receiverUsername, receiverAvatarUrl, receiverPoints, receiverCountry, haveListQuantityByCardIdMap }: Props) {
  const [editableCards, setEditableCards] = useState<TradeCard[]>(cards);
  const [timeRemaining, setTimeRemaining] = useState<number>(300); // 5 minutes in seconds
  const [calculatePrice, { isLoading: isCalculatingPrice }] = useCalculatePriceMutation();
  const [estimatePrice] = useEstimatePriceMutation();
  const [triggerGetCard] = useLazyGetCardByIdQuery();
  const isInitializedRef = useRef(false);

  // Layout switch: at 768px and below the modal is a full-screen sheet with
  // stacked rows and no hover preview (same breakpoint CardLink uses).
  const isMobile = useIsMobile();
  // Thumbnail hover preview: the shared Articles timing, one tooltip for the list.
  const hoverPreview = useCardHoverPreview({ disabled: isMobile });
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  // Send Points, stacked above this modal. The prefill is captured when it opens.
  const [sendPoints, setSendPoints] = useState<{ open: boolean; amount?: number; message?: string }>({ open: false });
  const sendPointsOpenRef = useRef(false);
  sendPointsOpenRef.current = sendPoints.open;

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
      setSendPoints({ open: false });
      setHoveredRowId(null);
    }
  }, [isOpen]);

  // Initialize cards when modal first opens - expand each card into individual per-row entries
  // Then recalculate prices to get correct condition-adjusted values from backend
  // Guard: only run on first open, not on subsequent prop changes from remote data updates
  useEffect(() => {
    if (isOpen && cards.length > 0 && !isInitializedRef.current) {
      isInitializedRef.current = true;
      const expandedRows: TradeCard[] = [];
      cards.forEach(card => {
        for (let i = 0; i < card.qty; i++) {
          expandedRows.push({
            ...card,
            id: `${card.wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            sourceCardId: card.id,
            qty: 1,
          });
        }
      });
      setEditableCards(expandedRows);

      // Recalculate prices so they reflect the actual condition (not just NM defaults)
      const recalculateInitialPrices = async () => {
        const cardsWithWantListId = expandedRows.filter(card => card.wantListId);
        if (cardsWithWantListId.length === 0) return;

        try {
          const result = await calculatePrice({
            cards: cardsWithWantListId.map(card => ({
              wantListId: card.wantListId!,
              cardId: card.cardId?.toString(),
              condition: card.condition,
              language: card.language,

              ...finishFields(haveFinishFromRow(card)),
              set: card.set
            }))
          }).unwrap();

          // Map prices by index — expanded rows share wantListId, so use 1:1 index correspondence
          setEditableCards(prev =>
            prev.map(card => {
              const reqIndex = cardsWithWantListId.findIndex(c => c.id === card.id);
              if (reqIndex !== -1 && reqIndex < result.cards.length) {
                const slotPrice = result.cards[reqIndex].displayPrice;
                // A null or non-positive slot is "no price", never a number to send at
                return { ...card, value: hasRealPrice(slotPrice) ? slotPrice : null };
              }
              return card;
            })
          );
        } catch (error) {
          console.error('Failed to recalculate initial prices:', error);
        }
      };
      recalculateInitialPrices();
    }
  }, [isOpen, cards, calculatePrice]);

  // Initialize timer only when modal opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      // Only reset timer when modal is first opened (transition from closed to open)
      if (lockExpiresAt) {
        const expiresAt = new Date(lockExpiresAt).getTime();
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(300); // Default to 5 minutes
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not lockExpiresAt which might change with polling

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          onClose(); // Auto-close when timer expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, timeRemaining, onClose]);

  // Escape closes the modal through the SAME handler as the X button, the
  // footer Cancel and the backdrop, so the trade lock is released. Before this
  // there was no keydown listener anywhere in this tree, so Escape was not a
  // close path at all.
  //
  // Placement is load-bearing: this must stay ABOVE the `if (!isOpen) return null`
  // early return below, with the other isOpen effects. A hook placed after that
  // return makes the hook count vary with isOpen and crashes React on the
  // open-to-close transition. Do not move it down.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // With Send Points open on top, Escape closes only that. The trade
        // modal, its lock and its countdown stay as they are.
        if (sendPointsOpenRef.current) {
          setSendPoints({ open: false });
          return;
        }
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Compute total quantity per cardId in the package (for "You Own" warning comparison — per printing)
  const packageQtyByCardId = useMemo(() => {
    const map = new Map<number, number>();
    editableCards.forEach(card => {
      if (card.cardId != null) {
        map.set(card.cardId, (map.get(card.cardId) || 0) + 1);
      }
    });
    return map;
  }, [editableCards]);

  // Group rows by wantListId for visual grouping (must be before early return)
  const groupedCards = useMemo(() => {
    const groups: Record<number, TradeCard[]> = {};
    const groupOrder: number[] = [];
    editableCards.forEach(card => {
      const key = card.wantListId!;
      if (!groups[key]) {
        groups[key] = [];
        groupOrder.push(key);
      }
      groups[key].push(card);
    });
    return { groups, groupOrder };
  }, [editableCards]);

  if (!isOpen) return null;

  // Server-side price calculation - secure against manipulation
  const fetchCardPrices = async (cardsToPrice: TradeCard[]): Promise<Map<number, number | null>> => {
    const priceMap = new Map<number, number | null>();
    
    // Filter cards that have wantListId for API call
    const cardsWithWantListId = cardsToPrice.filter(card => card.wantListId);
    
    if (cardsWithWantListId.length === 0) {
      // If no cards have wantListId, return current values as fallback
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, card.value);
        }
      });
      return priceMap;
    }
    
    try {
      const requestPayload = {
        cards: cardsWithWantListId.map(card => ({
          wantListId: card.wantListId!,
          cardId: card.cardId?.toString(),  // Specific card ID for exact version matching
          condition: card.condition,
          language: card.language,

          ...finishFields(haveFinishFromRow(card)),
          set: card.set
        }))
      };
      
      const result = await calculatePrice(requestPayload).unwrap();

      result.cards.forEach(cardPrice => {
        priceMap.set(cardPrice.wantListId, hasRealPrice(cardPrice.displayPrice) ? cardPrice.displayPrice : null);
      });
    } catch (error) {
      console.error('Failed to fetch prices from server:', error);
      // This runs only after a row's set, finish or condition changed. A lone
      // no-price card is refused with a 400, and on any other failure the old
      // number describes the previous card: either way the row has no price.
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, null);
        }
      });
    }
    
    return priceMap;
  };

  // Which finishes a modal card's printing exists in: the availableFinishes the
  // builder carried, else the legacy two flags.
  const modalFinishSource = (c: TradeCard): FinishSource =>
    c.availableFinishes
      ? { finishes: c.availableFinishes }
      : { availableInFoil: c.availableInFoil ?? false, availableInNonFoil: c.availableInNonFoil ?? true };

  const updateCard = async (cardId: string, updates: Partial<TradeCard>) => {
    const card = editableCards.find(c => c.id === cardId);
    if (!card) return;
    
    // Validate finish changes against physical availability AND receiver's preferences
    // (three-value: the offerable set is what the printing has and the want accepts)
    if ('finish' in updates || 'foil' in updates) {
      const requested = updates.finish ? fromHaveFinish(updates.finish) : (updates.foil ? 'foil' : 'nonfoil');
      if (!offerableFinishes(modalFinishSource(card), card).includes(requested)) {
        toast.warning(`This card is not available in ${FINISH_LABELS[requested]}`);
        return;
      }
      if (updates.finish) Object.assign(updates, finishFields(updates.finish));
    }

    const updatedCard = { ...card, ...updates };
    
    // If price-affecting fields changed, get new price from server
    // Set changes take priority — when set changes also resets language, use the set change pricing path
    if ('setCode' in updates && updatedCard.wantListId) {
      const priceMap = await fetchCardPrices([updatedCard]);
      const newPrice = priceMap.get(updatedCard.wantListId);
      if (newPrice !== undefined) {
        updatedCard.value = newPrice;
      }
    } else if ('language' in updates && updatedCard.availableLanguages) {
      // Language-only change (no set change)
      const langVariant = updatedCard.availableLanguages.find(
        (l) => l.lang === updatedCard.language
      );
      const estimateCardId = langVariant?.scryfallId || updatedCard.scryfallId;
      if (estimateCardId) {
        try {
          const result = await estimatePrice({
            cardId: estimateCardId,
            language: updatedCard.language,
            condition: updatedCard.condition,
            ...finishFields(haveFinishFromRow(updatedCard)),
          }).unwrap();
          const price = result.price ?? (result as any).data?.price;
          if (price != null) {
            updatedCard.value = price;
          }
        } catch {
          // Keep existing value on error
        }
      }
      // Update card image to match the selected language variant
      if (langVariant?.scryfallId) {
        updatedCard.image = AssetUrls.cards.getCardImage(langVariant.scryfallId, 'normal');
        updatedCard.imageSmall = AssetUrls.cards.getCardImage(langVariant.scryfallId, 'small');
      }
    } else if (('finish' in updates || 'foil' in updates || 'condition' in updates) && updatedCard.wantListId) {
      const priceMap = await fetchCardPrices([updatedCard]);
      const newPrice = priceMap.get(updatedCard.wantListId);
      if (newPrice !== undefined) {
        updatedCard.value = newPrice;
      }
    }
    
    setEditableCards(prev => 
      prev.map(c => c.id === cardId ? updatedCard : c)
    );
  };


  const addRow = (wantListId: number) => {
    const templateRow = editableCards.find(c => c.wantListId === wantListId);
    if (!templateRow) return;

    const currentCount = editableCards.filter(c => c.wantListId === wantListId).length;
    if (currentCount >= templateRow.maxQty) {
      toast.warning(`Receiver only wants ${templateRow.maxQty} copies of ${templateRow.name}`);
      return;
    }

    const newRow: TradeCard = {
      ...templateRow,
      id: `${wantListId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      qty: 1,
    };

    setEditableCards(prev => {
      const lastIndex = prev.map(c => c.wantListId).lastIndexOf(wantListId);
      const result = [...prev];
      result.splice(lastIndex + 1, 0, newRow);
      return result;
    });
  };

  const removeRow = (rowId: string) => {
    setEditableCards(prev => prev.filter(c => c.id !== rowId));
  };

  // Display totals - each row is qty=1
  const totalValue = sumRealPrices(editableCards.map(card => card.value));
  // A row with no price cannot be sent: the backend refuses it
  const hasNoPriceCard = editableCards.some(card => !hasRealPrice(card.value));
  const totalCards = editableCards.length;

  const handleInitiateTrade = () => {
    // All price validation happens server-side
    // Frontend prices are for display only
    onInitiateTrade(editableCards);
    // Don't call onClose() here - parent will close on success or handle errors
  };

  const openConditionGuide = () => {
    window.open('/articles/condition-guide', '_blank');
  };

  const openShippingGuide = () => {
    window.open('/articles/shipping', '_blank');
  };

  // Receiver funding. Unknown balance: no funding block and no funding gate
  // here (the server still enforces it at initiation).
  const funding = receiverPoints !== undefined ? getFundingState(totalValue, receiverPoints) : null;
  const underFunded = !!funding && !funding.funded;
  const name = receiverUsername || 'The receiver';
  const canSendPoints = receiverId !== undefined && !!receiverUsername;

  // Opened while under-funded, Send Points starts from the shortfall; otherwise empty, as on /sending.
  const openSendPoints = () => {
    if (!canSendPoints) return;
    setSendPoints(
      funding && !funding.funded
        ? {
            open: true,
            amount: funding.shortBy,
            message: `Covering the ${funding.shortBy}-point deficit on our trade`,
          }
        : { open: true },
    );
  };

  const hoveredCard = hoveredRowId ? editableCards.find((c) => c.id === hoveredRowId) : undefined;

  const timer = (
    <div className="timer-display itm-timer" style={{ color: timeRemaining < 60 ? '#ff6b6b' : '#999' }}>
      Time remaining: {formatTime(timeRemaining)}
    </div>
  );

  const coinIcon = <img src={AssetUrls.icons.profile.coin} alt="" className="itm-send-points-icon" />;

  const fundingBlock = funding && (
    <div className="itm-funding" data-testid="itm-funding" data-funded={funding.funded}>
      <div className="itm-funding-bar" role="img" aria-label={funding.funded ? `${name} can afford this trade` : `${name} is ${funding.shortBy} points short`}>
        <div className="itm-funding-bar-have" style={{ width: `${funding.fillRatio * 100}%` }} />
        {!funding.funded && <div className="itm-funding-bar-short" style={{ width: `${(1 - funding.fillRatio) * 100}%` }} />}
      </div>
      {funding.funded ? (
        <div className="itm-funding-legend">
          <div className="itm-funding-row">
            <span>This trade</span>
            <Points points={totalValue} type="inline" />
          </div>
          <div className="itm-funding-row">
            <span>{name}'s balance</span>
            <Points points={receiverPoints} type="inline" />
          </div>
          <div className="itm-funding-divider" />
          <div className="itm-funding-row">
            <span>{name}'s balance after trade</span>
            <Points points={funding.balanceAfter} type="inline" />
          </div>
        </div>
      ) : (
        <div className="itm-funding-legend">
          <div className="itm-funding-row">
            <span>{name}'s balance</span>
            <Points points={receiverPoints} type="inline" />
          </div>
          <div className="itm-funding-row itm-funding-row--short">
            <span>Short</span>
            <Points points={funding.shortBy} type="inline" tone="short" />
          </div>
        </div>
      )}
    </div>
  );

  const helpBlock = underFunded && funding && (
    <div className="itm-shortfall" data-testid="itm-shortfall">
      <p className="itm-shortfall-title">
        {name} is <Points points={funding.shortBy} type="inline" tone="short" /> short
      </p>
      <p className="itm-shortfall-body">Remove a card from this trade to initiate it.</p>
      {canSendPoints && funding.shortBy <= SEND_POINTS_HELPER_MAX_DEFICIT && (
        <button type="button" className="itm-shortfall-link" onClick={openSendPoints} data-testid="itm-shortfall-link">
          or send a few points to cover the difference
        </button>
      )}
    </div>
  );

  // A row with no price blocks the trade; say why beside the disabled button.
  const noPriceNotice = hasNoPriceCard && !isCalculatingPrice && (
    <p className="no-price-blocks-trade" role="status" data-testid="no-price-blocks-trade">
      {NO_PRICE_BLOCKS_TRADE}
    </p>
  );

  const guideLinks = (
    <div className="itm-guides">
      <button type="button" className="itm-guide-link" onClick={openConditionGuide}>
        Condition Guide
      </button>
      <button type="button" className="itm-guide-link" onClick={openShippingGuide}>
        Shipping Guide
      </button>
    </div>
  );

  const initiateButton = (
    <MagicButton
      label={isLoading ? "Initiating..." : "Initiate Trade"}
      color="purple"
      fullWidth
      onPress={handleInitiateTrade}
      isDisabled={editableCards.length === 0 || isLoading || totalValue <= 0 || isCalculatingPrice || underFunded || hasNoPriceCard}
      isLoading={isLoading}
    />
  );
  const cancelButton = <MagicButton label="Cancel" color="dark-grey" fullWidth onPress={onClose} isDisabled={isLoading} />;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`initiate-trade-modal itm ${isMobile ? 'itm--mobile' : 'itm--desktop'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header itm-header">
          <div className="itm-header-text">
            <h2 className="modal-title">{isMobile ? 'Finalize Your Trade' : 'Finalize Your Trade Package'}</h2>
            {isMobile && timer}
          </div>
          {!isMobile && timer}
          <button className="close-button itm-close" onClick={onClose} aria-label="Close">
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt=""
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="itm-body">
          <div className="itm-main">
            {isMobile && receiverUsername && (
              <div className="itm-mobile-summary">
                <AvatarWithFlag
                  avatarUrl={receiverAvatarUrl}
                  username={receiverUsername}
                  size={36}
                  countryCode={receiverCountry}
                />
                <div className="itm-mobile-summary-who">
                  <span className="receiver-username">{receiverUsername}</span>
                  {receiverPoints !== undefined && (
                    <span className="itm-mobile-summary-has">
                      Has <Points points={receiverPoints} type="inline" />
                    </span>
                  )}
                </div>
                <div className="itm-mobile-summary-totals">
                  <span className="summary-value">
                    <Points points={totalValue} type="inline" />
                  </span>
                  <span className="itm-mobile-summary-count">
                    <span className="summary-value">{totalCards}</span> {totalCards === 1 ? 'card' : 'cards'}
                  </span>
                </div>
                {canSendPoints && (
                  <button
                    type="button"
                    className="itm-send-points-btn itm-send-points-btn--icon"
                    aria-label="Send Points"
                    onClick={openSendPoints}
                    data-testid="itm-send-points"
                  >
                    {coinIcon}
                  </button>
                )}
              </div>
            )}

            <div className="itm-list" data-testid="itm-list">
              <div className="itm-list-header">
                <div className="itm-h-card">Card</div>
                <div className="itm-h-finish">Finish</div>
                <div className="itm-h-condition">Condition</div>
                <div className="itm-h-language">Lang</div>
                <div className="itm-h-own">Own</div>
                <div className="itm-h-value">Value</div>
                <div className="itm-h-remove" />
              </div>

              {groupedCards.groupOrder.map(wantListId => {
                const rows = groupedCards.groups[wantListId];
                if (!rows || rows.length === 0) return null;
                const firstCard = rows[0];

                return (
                  <div key={wantListId} className="card-group">
                    {rows.map((card, index) => (
                      <div key={card.id} className={`itm-row ${index > 0 ? 'continuation-row' : ''}`} data-testid="itm-row">
                        <div
                          className="itm-thumb"
                          onMouseEnter={(e) => {
                            setHoveredRowId(card.id);
                            hoverPreview.triggerHandlers.onMouseEnter(e);
                          }}
                          onMouseMove={hoverPreview.triggerHandlers.onMouseMove}
                          onMouseLeave={hoverPreview.triggerHandlers.onMouseLeave}
                        >
                          {(card.imageSmall || card.image) ? (
                            <FoilCardImage
                              src={card.imageSmall || card.image}
                              alt={card.name}
                              className="card-thumbnail"
                              finish={finishFromRow(card)}
                              borderRadius="4px"
                            />
                          ) : (
                            <div className="card-thumbnail card-thumbnail-placeholder" />
                          )}
                        </div>
                        <div className="itm-name" title={card.name}>{card.name}</div>

                        <div className="itm-set col-set">
                          {/* The symbol follows the selected printing. Beside the select, not in it:
                              native options cannot show images. Keyed so a set whose icon 404s
                              does not hide the next one. */}
                          <SetIcon key={card.setCode} setIconUrl={setIconUrl(card.setCode)} setName={card.set} size="md" />
                          {(() => {
                            const hasMultipleSetDetails = card.acceptableSetDetails && card.acceptableSetDetails.length > 1;
                            const hasMultipleSets = !card.acceptableSetDetails && card.acceptableSets && card.acceptableSets.length > 1;
                            const shouldShowDropdown = hasMultipleSetDetails || hasMultipleSets;

                            const setOptions: SetDetail[] = card.acceptableSetDetails ||
                              (card.acceptableSets?.map((name, idx) => ({
                                name: name,
                                code: card.acceptableSetCodes?.[idx] || name,
                                cardId: 0,
                              })) || []);

                            if (shouldShowDropdown) {
                              const currentValue = card.cardId
                                ? `${card.setCode}-${card.cardId}`
                                : card.setCode;

                              return (
                                <select
                                  value={currentValue}
                                  onChange={async (e) => {
                                    const selectedValue = e.target.value;
                                    const selectedSetDetail = setOptions.find(s => {
                                      const optionValue = s.cardId
                                        ? `${s.code}-${s.cardId}`
                                        : s.code;
                                      return optionValue === selectedValue;
                                    });
                                    if (selectedSetDetail) {
                                      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
                                      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

                                      const updates: Partial<TradeCard> = {
                                        setCode: selectedSetDetail.code,
                                        set: selectedSetDetail.name,
                                        availableInFoil: newAvailableInFoil,
                                        availableInNonFoil: newAvailableInNonFoil
                                      };

                                      if (selectedSetDetail.cardId) {
                                        updates.cardId = selectedSetDetail.cardId;
                                      }

                                      if (selectedSetDetail.imageUrl) {
                                        updates.image = selectedSetDetail.imageUrl;
                                        updates.imageSmall = selectedSetDetail.imageUrl
                                          ?.replace('/large/', '/small/')
                                          ?.replace('/normal/', '/small/');
                                      }

                                      // Fetch per-set availableLanguages via getCardById
                                      // The backend scopes availableLanguages by oracleId + setCode + collectorNumber,
                                      // so fetching by the new set's scryfallId returns only languages for that set
                                      const selectedScryfallId = selectedSetDetail.scryfallId;
                                      if (selectedScryfallId) {
                                        try {
                                          const cardData = await triggerGetCard(selectedScryfallId).unwrap();
                                          // Backend returns flat response — availableLanguages at top level
                                          const perSetLanguages = (cardData as any)?.availableLanguages as
                                            Array<{ lang: string; scryfallId: string; setCode?: string }> | undefined;
                                          if (perSetLanguages && perSetLanguages.length > 0) {
                                            updates.availableLanguages = perSetLanguages;
                                            updates.scryfallId = selectedScryfallId;

                                            // Reset language if current language not available in new set
                                            const currentLangAvailable = perSetLanguages.some(
                                              l => l.lang === card.language
                                            );
                                            if (!currentLangAvailable) {
                                              const enVariant = perSetLanguages.find(l => l.lang === 'en');
                                              const fallbackLang = enVariant ? 'en' : perSetLanguages[0]?.lang || 'en';
                                              updates.language = fallbackLang;
                                            }
                                          }
                                        } catch {
                                          // On failure, keep existing availableLanguages
                                        }
                                      }

                                      // Three-value auto-switch: keep the current finish when the new
                                      // printing has it and the want accepts it, else the first offerable
                                      const newSource: FinishSource = selectedSetDetail.availableFinishes
                                        ? { finishes: selectedSetDetail.availableFinishes }
                                        : { availableInFoil: newAvailableInFoil, availableInNonFoil: newAvailableInNonFoil };
                                      updates.availableFinishes = getAvailableFinishes(newSource);
                                      const offerableNow = offerableFinishes(newSource, card);
                                      if (!offerableNow.includes(finishFromRow(card))) {
                                        Object.assign(updates, finishFields(toHaveFinish(offerableNow[0] ?? getAvailableFinishes(newSource)[0])));
                                      }

                                      updateCard(card.id, updates);
                                    }
                                  }}
                                  className="set-select"
                                >
                                  {[...setOptions]
                                    .sort((a, b) => {
                                      const nameCompare = a.name.localeCompare(b.name);
                                      if (nameCompare !== 0) return nameCompare;
                                      const aNum = parseInt(a.collectorNumber || '0', 10);
                                      const bNum = parseInt(b.collectorNumber || '0', 10);
                                      return aNum - bNum;
                                    })
                                    .map(setDetail => {
                                      const uniqueValue = setDetail.cardId
                                        ? `${setDetail.code}-${setDetail.cardId}`
                                        : setDetail.code;
                                      const displayText = setDetail.collectorNumber
                                        ? `${setDetail.name} #${setDetail.collectorNumber}`
                                        : setDetail.name;
                                      return (
                                        <option key={uniqueValue} value={uniqueValue}>
                                          {displayText}
                                        </option>
                                      );
                                    })}
                                </select>
                              );
                            } else {
                              const singleDetail = card.acceptableSetDetails?.[0];
                              const collectorNum = singleDetail?.collectorNumber;
                              return <span>{card.set}{collectorNum && <span style={{ color: '#888' }}> #{collectorNum}</span>}</span>;
                            }
                          })()}
                        </div>

                        {/* One grid cell each on desktop (display: contents); a single flex row on mobile. */}
                        <div className="itm-controls">
                          <div className="itm-finish col-foil">
                          {/* D-3 price-carrying finish select; D-9: shown for two or more offerable finishes */}
                          <FinishSelect
                            value={finishFromRow(card)}
                            offerable={offerableFinishes(modalFinishSource(card), card)}
                            pointsByFinish={{ nonfoil: card.nonFoilValue, foil: card.foilValue, etched: card.etchedValue }}
                            baselineFinish={card.initialFinish ? fromHaveFinish(card.initialFinish) : undefined}
                            onChange={(f) => updateCard(card.id, finishFields(toHaveFinish(f)))}
                            selectClassName="foil-select"
                            staticClassName="foil-text"
                          />
                          </div>

                          <div className="itm-condition col-condition">
                          {(() => {
                            const allConditions = [
                              { value: 'NEAR_MINT', label: 'NM' },
                              { value: 'SLIGHTLY_PLAYED', label: 'LP' },
                              { value: 'MODERATELY_PLAYED', label: 'MP' },
                              { value: 'HEAVILY_PLAYED', label: 'HP' },
                            ];

                            const acceptableConditions = card.acceptableConditions ||
                              allConditions.map(c => c.value);
                            const availableOptions = allConditions.filter(c =>
                              acceptableConditions.includes(c.value)
                            );

                            if (availableOptions.length > 1) {
                              return (
                                <select
                                  value={card.condition}
                                  onChange={(e) => updateCard(card.id, { condition: e.target.value })}
                                  className="condition-select"
                                >
                                  {availableOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              );
                            } else {
                              const singleCondition = availableOptions[0] || allConditions[0];
                              return <span className="condition-text">{singleCondition.label}</span>;
                            }
                          })()}
                          </div>

                          <div className="itm-language col-language">
                          {(() => {
                            const langDisplayMap: Record<string, string> = { en: 'EN', fr: 'FR', de: 'DE', es: 'ES', it: 'IT', ja: 'JP' };
                            const allLangs = [
                              { value: 'en', label: 'EN' }, { value: 'fr', label: 'FR' },
                              { value: 'de', label: 'DE' }, { value: 'es', label: 'ES' },
                              { value: 'it', label: 'IT' }, { value: 'ja', label: 'JP' },
                            ];

                            const languageOptions = card.acceptableLanguages?.length
                              ? (card.availableLanguages
                                  ?.filter(l => card.acceptableLanguages!.includes(l.lang))
                                  .map(l => ({ value: l.lang, label: langDisplayMap[l.lang] || l.lang.toUpperCase() }))
                                ) || allLangs.filter(l => card.acceptableLanguages!.includes(l.value))
                              : card.availableLanguages?.map(l => ({ value: l.lang, label: langDisplayMap[l.lang] || l.lang.toUpperCase() }))
                                || allLangs;

                            return (
                              <select
                                value={card.language}
                                onChange={(e) => updateCard(card.id, { language: e.target.value })}
                                className="language-select"
                              >
                                {languageOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            );
                          })()}
                          </div>

                          {(() => {
                            const youOwnQty = card.cardId != null ? (haveListQuantityByCardIdMap?.get(card.cardId) ?? 0) : 0;
                            const pkgQty = card.cardId != null ? (packageQtyByCardId.get(card.cardId) ?? 0) : 0;
                            const isWarning = pkgQty > youOwnQty;
                            return (
                              <div className={`itm-own col-you-own${isWarning ? ' col-you-own--warning' : ''}`}>
                                <span className="itm-own-label">Own </span>
                                {youOwnQty}
                              </div>
                            );
                          })()}

                          <div className="itm-value col-value">
                            {hasRealPrice(card.value) ? <Points points={card.value} type="small" /> : <NoPrice />}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="itm-remove col-remove"
                          aria-label={`Remove ${card.name}`}
                          onClick={() => removeRow(card.id)}
                        >
                          <img src={AssetUrls.icons.dropdowns.close} alt="" className="remove-icon" />
                        </button>
                      </div>
                    ))}
                    {rows.length < firstCard.maxQty && (
                      <div className="add-copy-row">
                        <button
                          className="add-copy-btn"
                          onClick={() => addRow(wantListId)}
                        >
                          + Add copy ({rows.length}/{firstCard.maxQty})
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {isMobile && guideLinks}
            </div>

            {isMobile && (
              <div className="itm-mobile-bottom">
                {fundingBlock}
                {helpBlock}
                {noPriceNotice}
                <div className="itm-mobile-actions">
                  {cancelButton}
                  {initiateButton}
                </div>
              </div>
            )}
          </div>

          {!isMobile && (
            <aside className="itm-rail" data-testid="itm-rail">
              {receiverUsername && (
                <div className="itm-receiver">
                  <AvatarWithFlag
                    avatarUrl={receiverAvatarUrl}
                    username={receiverUsername}
                    size={40}
                    countryCode={receiverCountry}
                  />
                  <div className="itm-receiver-who">
                    <span className="itm-receiver-label">Sending to</span>
                    <span className="receiver-username">{receiverUsername}</span>
                  </div>
                  {canSendPoints && (
                    <button type="button" className="itm-send-points-btn" onClick={openSendPoints} data-testid="itm-send-points">
                      {coinIcon}
                      Send Points
                    </button>
                  )}
                </div>
              )}

              <div className="itm-stats">
                <div className="itm-stat">
                  <span className="summary-label">Total Cards</span>
                  <span className="summary-value">{totalCards}</span>
                </div>
                <div className="itm-stat">
                  <span className="summary-label">Total Value</span>
                  <span className="summary-value">
                    <Points points={totalValue} type="inline" />
                  </span>
                </div>
              </div>

              {fundingBlock}
              {helpBlock}
              {noPriceNotice}

              <div className="itm-rail-spacer" />

              {guideLinks}
              <div className="itm-rail-actions">
                {initiateButton}
                {cancelButton}
              </div>
            </aside>
          )}
        </div>

        {canSendPoints && (
          <SendPointsModal
            isOpen={sendPoints.open}
            onClose={() => setSendPoints({ open: false })}
            recipientId={receiverId as number}
            recipientUsername={receiverUsername as string}
            initialAmount={sendPoints.amount}
            initialMessage={sendPoints.message}
          />
        )}

        {/* The Articles card preview, showing the row's SELECTED printing: `card.image`
            is updated with the set and language selects, so the preview matches them. */}
        {hoverPreview.showTooltip && !isMobile && hoveredCard && (hoveredCard.image || hoveredCard.imageSmall) && (
          <CardImageTooltip
            imageUrl={hoveredCard.image || hoveredCard.imageSmall}
            cardName={hoveredCard.name}
            position={hoverPreview.position}
            {...hoverPreview.tooltipHandlers}
          />
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
